import { useEffect, useRef, useCallback } from "react";

interface LongPressParams {
  onLongPress: () => void;
  onPressEnd: () => void;
  delayMs?: number;
}

export function useLongPress(
  ref: React.RefObject<HTMLElement>,
  { onLongPress, onPressEnd, delayMs = 500 }: LongPressParams
) {
  const timerRef = useRef<number | null>(null);
  const longPressedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handlePointerDown = () => {
      longPressedRef.current = false;
      clear();
      timerRef.current = window.setTimeout(() => {
        longPressedRef.current = true;
        onLongPress();
      }, delayMs);
    };

    const handlePointerUpOrCancel = () => {
      clear();
      onPressEnd();
    };

    const handleContextMenu = (e: Event) => e.preventDefault();
    const endEvents = ["pointerup", "pointercancel", "pointerleave"];

    element.addEventListener("pointerdown", handlePointerDown);
    element.addEventListener("contextmenu", handleContextMenu);
    endEvents.forEach((evt) => element.addEventListener(evt, handlePointerUpOrCancel));

    return () => {
      clear();
      element.removeEventListener("pointerdown", handlePointerDown);
      element.removeEventListener("contextmenu", handleContextMenu);
      endEvents.forEach((evt) => element.removeEventListener(evt, handlePointerUpOrCancel));
    };
  }, [ref, onLongPress, onPressEnd, delayMs, clear]);

  return {
    wasLongPressedRef: longPressedRef,
  };
}